// src/components/I18nProvider.tsx
'use client'

import { createContext, useContext } from 'react'
import { DEFAULT_LOCALE, t as translate } from '@/lib/content/i18n'
import type { Locale, TranslationKey } from '@/lib/content/i18n'

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey) => string
}

// Language switcher was removed because translations were incomplete.
// We keep the i18n machinery and ~900 t() call sites in place, but force
// the locale to English so stale `locale` cookies/localStorage from before
// the dropdown was removed don't strand users in a half-translated UI.
//
// To re-enable a real language switcher in the future:
//   1. Restore the cookie/localStorage hydration that previously lived here
//      (see git history before this change).
//   2. Re-mount <LanguageSelector /> in src/components/site-nav.tsx.
//   3. Audit translation completeness in src/locales/* before exposing it.
const FORCED_LOCALE: Locale = DEFAULT_LOCALE

const i18nValue: I18nContextValue = {
  locale: FORCED_LOCALE,
  setLocale: () => {
    // no-op: language switching is disabled
  },
  t: (key) => translate(key, FORCED_LOCALE),
}

const I18nContext = createContext<I18nContextValue>(i18nValue)

export function I18nProvider({ children }: { children: React.ReactNode }) {
  return <I18nContext.Provider value={i18nValue}>{children}</I18nContext.Provider>
}

/** Hook to access translations + locale from any client component */
export function useI18n() {
  return useContext(I18nContext)
}
